import { g } from "../util/index.ts";
import { currentRoundBids, type FaBid } from "./fa.ts";
import { getLeagueIdForLid, getLobbyState, getSession } from "./index.ts";
import { listResignCandidates, type ResignCandidate } from "./resign.ts";

// State the normal Re-sign Players and Free Agents pages need to host the
// multiplayer flows (instead of panels on the Multiplayer page). undefined =
// single player league, act like vanilla BBGM.
export type MpPageState = {
	leagueId: string;
	// Re-sign window: each human re-signs their own expiring players
	resignActive: boolean;
	myResignCandidates: ResignCandidate[];
	// Blind-bid free agency auction
	faOpen: boolean;
	faRound: number;
	myBids: FaBid[];
	// Normal first-come-first-serve signing (any time outside the auction, a
	// sim, or the live draft) - like signing free agents in vanilla BBGM
	directSignOpen: boolean;
	// For the runner's "Resolve Round Now" fallback on the FA page
	isRunner: boolean;
	allReady: boolean;
};

export const getMpPageState = async (): Promise<MpPageState | undefined> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return undefined;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return undefined;
	}

	const state: MpPageState = {
		leagueId,
		resignActive: false,
		myResignCandidates: [],
		faOpen: false,
		faRound: 0,
		myBids: [],
		directSignOpen: false,
		isRunner: false,
		allReady: false,
	};

	try {
		const session = await getSession();
		if (!session) {
			return state;
		}
		const { league, members } = await getLobbyState(leagueId);
		const me = members.find((m) => m.user_id === session.userId);

		state.isRunner =
			session.userId === (league.sim_runner ?? league.commissioner);
		state.allReady = members.length > 0 && members.every((m) => m.ready);

		if (league.checkpoint === "resign" && !league.sim_open) {
			state.resignActive = true;
			if (me?.tid !== null && me?.tid !== undefined) {
				const all = await listResignCandidates(leagueId);
				state.myResignCandidates = all.filter((c) => c.tid === me.tid);
			}
		}

		if (league.fa_open) {
			state.faOpen = true;
			state.faRound = league.fa_round;
			const bids = await currentRoundBids(leagueId, league.fa_round);
			state.myBids = bids.filter((b) => b.user_id === session.userId);
		}

		// Outside the auction, a sim, and the live draft, free agents can be
		// signed directly (first-come-first-serve). Needs a claimed team.
		state.directSignOpen =
			!league.fa_open &&
			!league.sim_open &&
			!league.draft_active &&
			me?.tid !== null &&
			me?.tid !== undefined;
	} catch {
		// Network hiccup: render the page without multiplayer extras rather than
		// erroring the whole view. The Multiplayer page surfaces real errors.
	}

	return state;
};
